import { computed, Directive, inject, input, Signal } from '@angular/core';
import { ElementIntent, FEATURE_ID, ModuleKey } from '@cosmos/shared';
import { EntitlementService } from '@cosmos/angular/utils';

/**
 * Attribute directive that enforces element-level access control based on
 * project lifecycle state, module billing state, feature entitlement, and
 * quota. Exports disabled() and tooltip() signals for the caller to bind to
 * whatever host element they have — works for <button>, <p-button>, <input>,
 * or any future component without internal DOM mutation.
 *
 * For the 'hidden' case (project inactive + mutate/explore intent), the
 * caller is responsible for wrapping the element in @if:
 *
 *   @if (entitlementService.projectAccessState('analytics')() !== 'inactive') {
 *     <button
 *       pButton
 *       [cosmosAction]="'mutate'"
 *       [cosmosActionModule]="'analytics'"
 *       [cosmosActionFeature]="'analytics.create_pour'"
 *       #a="cosmosAction"
 *       [disabled]="a.disabled()"
 *       [pTooltip]="a.tooltip() | translate"
 *       tooltipPosition="top">
 *       Create Pour
 *     </button>
 *   }
 *
 * Decision tree (checked in order, cheapest first):
 *   1. module locked                  → disabled + 'access.module-locked'
 *   2. feature not in features[]     → disabled + 'access.feature-unavailable'
 *   3. bucket exhausted              → disabled + 'access.quota-exhausted'
 *   4.                               → enabled (disabled=false, tooltip='')
 *
 * Note: 'inactive' project state is not handled here — use @if with
 * projectAccessState() at the call site (see usage doc).
 * Note: 'preview' is handled by ModuleAccessGuard at route level.
 */
@Directive({
  selector: '[cosmosAction]',
  exportAs: 'cosmosAction',
  standalone: true,
})
export class ActionDirective {
  private readonly entitlementService = inject(EntitlementService);

  /** What this element does — required. */
  readonly cosmosAction = input.required<ElementIntent>();

  /** The module this element belongs to — required. */
  readonly cosmosActionModule = input.required<ModuleKey>();

  /**
   * Optional feature key. When provided, tier gate (step 2) and quota check
   * (step 3) are evaluated. When omitted, only module locked state is checked.
   */
  readonly cosmosActionFeature = input<FEATURE_ID | null>(null);

  // ── Exported signals — bind these to the host element ─────────────────────

  readonly disabled: Signal<boolean> = computed(() => this._tooltipKey() !== '');

  readonly tooltip: Signal<string> = computed(() => this._tooltipKey());

  // ── Internal ──────────────────────────────────────────────────────────────

  private readonly _tooltipKey = computed<string>(() => {
    const module = this.cosmosActionModule();
    const feature = this.cosmosActionFeature();
    const projectState = this.entitlementService.projectAccessState(module)();

    // Step 1: module locked — disable all non-read actions
    if (projectState === 'locked' && this.cosmosAction() !== 'read') {
      return 'access.module-locked';
    }

    if (feature) {
      // Step 2: feature not in tier
      if (!this.entitlementService.hasFeature(module, feature)) {
        return 'access.feature-unavailable';
      }

      // Step 3: bucket exhausted
      const bucket = this.entitlementService.getBucket(module, feature as any);
      if (bucket && !bucket.allowNegative && bucket.remaining <= 0) {
        return 'access.quota-exhausted';
      }
    }

    return '';
  });
}
